import { api } from './client';

export type SessionStatus = 'IN_PROGRESS' | 'FINISHED' | 'CANCELLED';
export type PickStatus = 'TO_PICK' | 'PICKED' | 'SKIPPED' | 'NOT_FOUND';

export interface ShoppingSession {
  id: string;
  storeName: string;
  status: SessionStatus;
  startedAt: string;
  finishedAt: string | null;
  itemCount?: number;
  pickedCount?: number;
  total?: number | null;
}

export interface ShoppingSessionItem {
  id: string;
  shoppingSessionId: string;
  householdItemId: string | null;
  itemNameSnapshot: string;
  quantityAtHomeSnapshot: string | null;
  quantityToBuy: string;
  pickStatus: PickStatus;
  unitPrice: string | null;
  totalPrice: string | null;
  lastStoreName: string | null;
  lastUnitPrice: string | null;
  brand: string | null;
  addedDuringShopping: boolean;
}

export interface StartSessionPayload {
  storeName: string;
}

export interface UpdatePickStatusPayload {
  pickStatus: PickStatus;
  unitPrice?: string | null;
  /** New home stock for the linked item (ADR 031 — user-confirmed) */
  quantityAtHome?: string | null;
}

export interface AddItemDuringShoppingPayload {
  itemName: string;
  quantityToBuy: string;
  unitPrice?: string | null;
  brand?: string | null;
}

export const shoppingApi = {
  listSessions: (params?: { status?: SessionStatus }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    return api.get<ShoppingSession[]>(`/sessions${qs.size ? '?' + qs : ''}`);
  },

  getActiveSession: () => api.get<ShoppingSession | null>('/sessions/active'),

  getSession: (id: string) => api.get<ShoppingSession>(`/sessions/${id}`),

  startSession: (payload: StartSessionPayload) =>
    api.post<ShoppingSession>('/sessions', payload),

  cancelSession: (id: string) =>
    api.post<ShoppingSession>(`/sessions/${id}/cancel`, {}),

  finishSession: (id: string) =>
    api.post<ShoppingSession>(`/sessions/${id}/finish`, {}),

  getSessionItems: (sessionId: string) =>
    api.get<ShoppingSessionItem[]>(`/sessions/${sessionId}/items`),

  updatePickStatus: (sessionId: string, itemId: string, payload: UpdatePickStatusPayload) =>
    api.patch<ShoppingSessionItem>(
      `/sessions/${sessionId}/items/${itemId}`,
      payload
    ),

  addItemDuringShopping: (sessionId: string, payload: AddItemDuringShoppingPayload) =>
    api.post<ShoppingSessionItem>(`/sessions/${sessionId}/items`, {
      // Backend contract uses `name`, not `itemName` — mismatch here previously caused
      // "Nome do item é obrigatório" (MISSING_NAME) even with the field filled in the UI.
      name: payload.itemName,
      quantityToBuy: payload.quantityToBuy,
      unitPrice: payload.unitPrice,
      brand: payload.brand,
    }),

  repeatSession: (id: string, storeName?: string) =>
    api.post<ShoppingSession>(`/sessions/${id}/repeat`, { storeName }),
};
